import { useState, useEffect } from "react";
import { useGet, usePost } from "./useFetch";
import useGaEvent from "./useGaEvent";

const useTrainingCourse = () => {
  // GA Events
  const {
    trainingCourseVisit,
    trainingCourseAddVisit,
    trainingCourseAddSuccess,
    trainingCourseAddFail,
    trainingCourseUpdateVisit,
    trainingCourseUpdateSuccess,
    trainingCourseUpdateFail,
    trainingCourseDeleteVisit,
    trainingCourseDeleteSuccess,
    trainingCourseDeleteFail,
    competencyAssessmentVisit,
    competencyAssessmentSuccess,
    competencyAssessmentFail,
    competencyQuizVisit,
  } = useGaEvent();

  // API hooks
  const [coursesResponse, coursesError, coursesLoading, getCourses,] = useGet();
  const [courseResponse, courseError, courseLoading, getCourse,] = useGet();
  const [modulesResponse, modulesError, modulesLoading, getModules,] = useGet();
  const [competenciesResponse, competenciesError, competenciesLoading, getCompetencies,] = useGet();
  const [enrollmentsResponse, enrollmentsError, enrollmentsLoading, getEnrollments,] = useGet();
  const [enrollmentResponse, enrollmentError, enrollmentLoading, getEnrollment,] = useGet();
  const [assessmentsResponse, assessmentsError, assessmentsLoading, getAssessments,] = useGet();
  const [assessmentResponse, assessmentError, assessmentLoading, getAssessment,] = useGet();
  const [candidateQuestionsResponse, candidateQuestionsError, candidateQuestionsLoading, getCandidateQuestions,] = useGet();
  const [practicalProvidersResponse, practicalProvidersError, practicalProvidersLoading, getPracticalProviders,] = useGet();
  
  const [createEnrollmentResponse, createEnrollmentError, createEnrollmentLoading, createEnrollment,] = usePost();
  const [updateEnrollmentResponse, updateEnrollmentError, updateEnrollmentLoading, updateEnrollment,] = usePost();
  const [deleteEnrollmentResponse, deleteEnrollmentError, deleteEnrollmentLoading, deleteEnrollment,] = usePost();
  const [createAssessmentResponse, createAssessmentError, createAssessmentLoading, createAssessment,] = usePost();
  const [evaluateAssessmentResponse, evaluateAssessmentError, evaluateAssessmentLoading, evaluateAssessment,] = usePost();

  // State for managing data
  const [courses, setCourses] = useState([]);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [candidateQuestions, setCandidateQuestions] = useState([]);
  const [evaluateAssessmentResult, setEvaluateAssessmentResult] = useState(null);
  const [practicalProviders, setPracticalProviders] = useState([]);

  // Fetch all courses
  const fetchCourses = async () => {
    trainingCourseVisit();
    await getCourses("/api/trainingcourse/courses");
  };

  // Fetch specific course
  const fetchCourse = async (courseId) => {
    trainingCourseVisit();
    await getCourse(`/api/trainingcourse/courses/${courseId}`);
  };

  // Fetch all modules
  const fetchModules = async () => {
    await getModules("/api/trainingcourse/modules");
  };

  // Fetch all competencies
  const fetchCompetencies = async () => {
    await getCompetencies("/api/trainingcourse/competencies");
  };

  // Fetch user enrollments
  const fetchUserEnrollments = async () => {
    await getEnrollments("/api/trainingcourse/enrollments");
  };

  // Fetch specific enrollment
  const fetchEnrollment = async (enrollmentId) => {
    await getEnrollment(`/api/trainingcourse/enrollments/${enrollmentId}`);
  };

  // Fetch user assessments
  const fetchUserAssessments = async () => {
    await getAssessments("/api/trainingcourse/assessments");
  };

  // Fetch specific assessment with full details
  const fetchAssessment = async (assessmentId) => {
    competencyAssessmentVisit();
    await getAssessment(`/api/trainingcourse/assessment/${assessmentId}`);
  };

  // Fetch candidate questions for quiz
  const fetchCandidateQuestions = async (competencyAssessmentId) => {
    await getCandidateQuestions(`/api/trainingcourse/candidateQuestions/${competencyAssessmentId}`);
  };

  // Fetch practical providers
  const fetchPracticalProviders = async () => {
    await getPracticalProviders('/api/trainingcourse/practicalProviders');
  };

  // Helper function to get assessment by ID
  const getAssessmentById = (assessmentId) => {
    return assessments.find(assessment => assessment.competencyAssessmentId === assessmentId);
  };

  // Create course enrollment
  const enrollInCourse = async (enrollmentData) => {
    trainingCourseAddVisit();
    await createEnrollment("/api/trainingcourse/enrollments", enrollmentData, false, null,
      () => {
        // Refresh enrollments after successful enrollment
        trainingCourseAddSuccess();
        fetchUserEnrollments();
      },
      () => {
        trainingCourseAddFail();
      }
    );
  };

  // Update course enrollment
  const updateCourseEnrollment = async (enrollmentId, enrollmentData) => {
    trainingCourseUpdateVisit();
    await updateEnrollment(`/api/trainingcourse/enrollments/${enrollmentId}`, enrollmentData, false,
      () => {
        trainingCourseUpdateSuccess();
      },
      () => {
        trainingCourseUpdateFail();
      }
    );
  };

  // Delete course enrollment
  const deleteCourseEnrollment = async (enrollmentId) => {
    trainingCourseDeleteVisit();
    await deleteEnrollment(`/api/trainingcourse/enrollments/${enrollmentId}`, {}, false,
      () => {
        trainingCourseDeleteSuccess();
      },
      () => {
        trainingCourseDeleteFail();
      }
    );
  };

  // Create competency assessment
  const startAssessment = async (assessmentData) => {
    await createAssessment("/api/trainingcourse/assessments", assessmentData, false,
      () => {
        competencyAssessmentSuccess();
      },
      () => {
        competencyAssessmentFail();
      }
    );
  };

  // Evaluate competency assessment
  const evaluateCompetencyAssessment = async (evaluateData) => {
    await evaluateAssessment("/api/trainingcourse/evaluate-assessment", evaluateData, false);
  };

  // Helper function to check if user is enrolled in a course
  const isEnrolledInCourse = (courseId) => {
    return userEnrollments.some(enrollment => enrollment.courseId === courseId);
  };

  // Helper function to get enrollment for a course
  const getCourseEnrollment = (courseId) => {
    return userEnrollments.find(enrollment => enrollment.courseId === courseId);
  };

  // Helper function to get modules for a course
  const getCourseModules = (courseId) => {
    if (!currentCourse || currentCourse.courseId !== courseId) {
      return [];
    }
    // Use the course's navigation property - now includes full Module objects
    return currentCourse.dlTrainingCourseModules?.sort((a, b) => (a.moduleListOrder || 0) - (b.moduleListOrder || 0)) || [];
  };

  // Helper function to get competencies for a module with isEnabledCompetency computed
  const getModuleCompetencies = (moduleId) => {
    if (competencies.length === 0) return [];

    // Find the first non-passed competency in the module
    const ordered = [...competencies]
      .filter(c => c.moduleId === moduleId)
      .sort((a, b) => a.competencyListOrder - b.competencyListOrder);
    const firstNonPassed = ordered.find(c => c.competencyAssessmentStatus !== "P");

    return competencies
      .filter(c => c.moduleId === moduleId)
      .sort((a, b) => a.competencyListOrder - b.competencyListOrder)
      .map(c => ({
        ...c,
        isEnabledCompetency: firstNonPassed
          ? c.competencyId === firstNonPassed.competencyId
          : c.competencyListOrder === 1,
      }));
  };

  // Update state when API responses change
  useEffect(() => {
    if (coursesResponse) {
      setCourses(coursesResponse);
    }
  }, [coursesResponse]);

  useEffect(() => {
    if (courseResponse) {
      setCurrentCourse(courseResponse);
    }
  }, [courseResponse]);

  useEffect(() => {
    if (modulesResponse) {
      setModules(modulesResponse);
    }
  }, [modulesResponse]);

  useEffect(() => {
    if (competenciesResponse) {
      setCompetencies(competenciesResponse);
    }
  }, [competenciesResponse]);

  useEffect(() => {
    if (enrollmentsResponse) {
      setUserEnrollments(enrollmentsResponse);
    }
  }, [enrollmentsResponse]);

  useEffect(() => {
    if (assessmentsResponse) {
      setAssessments(assessmentsResponse);
    }
  }, [assessmentsResponse]);

  useEffect(() => {
    if (assessmentResponse) {
      setCurrentAssessment(assessmentResponse);
    }
  }, [assessmentResponse]);

  useEffect(() => {
    if (candidateQuestionsResponse) {
      setCandidateQuestions(candidateQuestionsResponse);
    }
  }, [candidateQuestionsResponse]);

  useEffect(() => {
    if (practicalProvidersResponse) {
      setPracticalProviders(practicalProvidersResponse);
    }
  }, [practicalProvidersResponse]);

  useEffect(() => {
    if (evaluateAssessmentResponse) {
      // Response received from evaluation, can be used for success handling
      // The response should contain: { isPass, assessmentResult, message }
      // console.log("Evaluation response received:", evaluateAssessmentResponse);
      setEvaluateAssessmentResult(evaluateAssessmentResponse);
    }
  }, [evaluateAssessmentResponse]);

  return {
    // Data
    courses,
    currentCourse,
    modules,
    competencies,
    enrollments,
    userEnrollments,
    assessments,
    currentAssessment,
    candidateQuestions,
    evaluateAssessmentResult,
    practicalProviders,

    // Loading states
    coursesLoading,
    courseLoading,
    modulesLoading,
    competenciesLoading,
    enrollmentsLoading,
    enrollmentLoading,
    assessmentsLoading,
    assessmentLoading,
    candidateQuestionsLoading,
    practicalProvidersLoading,
    createEnrollmentLoading,
    updateEnrollmentLoading,
    deleteEnrollmentLoading,
    createAssessmentLoading,
    evaluateAssessmentLoading,

    // Error states
    coursesError,
    courseError,
    modulesError,
    competenciesError,
    enrollmentsError,
    enrollmentError,
    assessmentsError,
    assessmentError,
    candidateQuestionsError,
    practicalProvidersError,
    createEnrollmentError,
    updateEnrollmentError,
    deleteEnrollmentError,
    createAssessmentError,
    evaluateAssessmentError,

    // API responses for success handling
    createEnrollmentResponse,
    updateEnrollmentResponse,
    deleteEnrollmentResponse,
    createAssessmentResponse,

    // Methods
    fetchCourses,
    fetchCourse,
    fetchModules,
    fetchCompetencies,
    fetchUserEnrollments,
    fetchEnrollment,
    fetchUserAssessments,
    fetchAssessment,
    fetchCandidateQuestions,
    fetchPracticalProviders,
    enrollInCourse,
    updateCourseEnrollment,
    deleteCourseEnrollment,
    startAssessment,
    evaluateCompetencyAssessment,

    // Helper methods
    isEnrolledInCourse,
    getCourseEnrollment,
    getCourseModules,
    getModuleCompetencies,
    getAssessmentById,
  };
};

export default useTrainingCourse;